import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ArrowLeft, X, Camera, FolderOpen, FileText, Image as ImageIcon, Loader2, CheckCircle2 } from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { usePortalDestination, usePortalDraft } from '../PortalDestinationContext';
import { PortalDestinationPicker } from '../PortalIdentity';

const STEPS = ['destination', 'capture', 'preview', 'details', 'review', 'confirmation'];
const STEP_TITLES = {
  destination: 'Choose pharmacy', capture: 'Add your prescription', preview: 'Preview',
  details: 'Add details', review: 'Review & send', confirmation: 'Sent',
};

/**
 * Mobile full-screen prescription upload sequence. Submission logic and
 * endpoints are unchanged. `initialFile` lets a "Reorder" shortcut on a past
 * prescription hand in an already-fetched File so the patient skips straight
 * to preview instead of picking a file from their device again. The caller
 * only supplies it when a matching branch is already selected (same rule as
 * the Refill/Order reorder buttons) — choosing a destination navigates and
 * would otherwise wipe this component's state before the file is sent.
 */
export const PortalPrescriptionUploadFlow = ({ onClose, onUploaded, initialFile = null }) => {
  const { api: portalApi, branch, destinationLabel, ensureConnection } = usePortalDestination();
  const hasSeed = !!(initialFile && branch);
  const [stepIdx, setStepIdx] = useState(() => {
    if (hasSeed) return STEPS.indexOf('preview');
    return branch ? 1 : 0;
  });
  const [file, setFile] = useState(hasSeed ? initialFile : null);
  const [previewUrl, setPreviewUrl] = useState(
    hasSeed && initialFile.type.startsWith('image/') ? URL.createObjectURL(initialFile) : ''
  );
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const cameraRef = useRef(null);
  const fileRef = useRef(null);
  const step = STEPS[stepIdx];
  usePortalDraft(!!file || notes.trim(), uploading, file ? `your prescription photo (${file.name})` : (notes.trim() ? 'your prescription notes' : ''));

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const pickFile = (f) => {
    if (!f) return;
    setFile(f);
    setPreviewUrl(f.type.startsWith('image/') ? URL.createObjectURL(f) : '');
    setStepIdx(STEPS.indexOf('preview'));
  };

  const goBack = () => {
    if (stepIdx === 0 || (stepIdx === 1 && !branch)) { onClose(); return; }
    setStepIdx((i) => Math.max(0, i - 1));
  };
  const goNext = () => setStepIdx((i) => {
    const next = Math.min(STEPS.length - 2, i + 1);
    // A reorder shortcut already supplied a file — skip the capture step.
    return STEPS[next] === 'capture' && file ? STEPS.indexOf('preview') : next;
  });

  const submit = async () => {
    if (!branch || !file) return;
    setUploading(true);
    try {
      if (!(await ensureConnection())) { setUploading(false); return; }
      const fd = new FormData();
      fd.append('file', file);
      const params = new URLSearchParams();
      if (notes.trim()) params.set('notes', notes.trim());
      params.set('tenant_id', branch.tenant_id);
      const result = await portalApi.post(`/prescriptions?${params.toString()}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const message = `Prescription sent to ${result.data.branch_name}.`;
      toast.success(message);
      setConfirmation(message);
      setStepIdx(STEPS.indexOf('confirmation'));
      onUploaded?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const primaryLabel = step === 'review' ? (uploading ? 'Sending…' : 'Send to pharmacist') : 'Continue';
  const primaryDisabled = step === 'destination' && !branch;

  return (
    <div className="portal-flow" data-testid="portal-rx-upload-flow">
      <div className="portal-flow-header">
        <button type="button" onClick={step === 'confirmation' ? onClose : goBack}
          aria-label={step === 'confirmation' ? 'Close' : 'Back'} data-testid="portal-rx-flow-back">
          {step === 'confirmation' ? <X size={20} /> : <ArrowLeft size={20} />}
        </button>
        <h1>{STEP_TITLES[step]}</h1>
        <span aria-hidden="true" />
      </div>

      {step !== 'confirmation' && (
        <div className="portal-flow-steps" aria-hidden="true">
          {STEPS.slice(0, -1).map((s, i) => <span key={s} className={i <= stepIdx ? 'is-done' : ''} />)}
        </div>
      )}

      <div className="portal-flow-body">
        {step === 'destination' && (
          <div className="space-y-3">
            <p className="text-sm text-slate-500">Choose the pharmacy and branch that will receive this prescription.</p>
            <PortalDestinationPicker bare />
          </div>
        )}

        {step === 'capture' && (
          <div data-testid="portal-rx-flow-capture-step">
            <p className="text-sm text-slate-500 mb-3">Snap a photo or upload a file. Accepted: images or PDF.</p>
            <button type="button" className="portal-flow-choice" onClick={() => cameraRef.current?.click()} data-testid="portal-rx-flow-camera">
              <Camera size={22} /> Take a photo
            </button>
            <button type="button" className="portal-flow-choice" onClick={() => fileRef.current?.click()} data-testid="portal-rx-flow-filepicker">
              <FolderOpen size={22} /> Choose a file
            </button>
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="absolute opacity-0 w-0 h-0 overflow-hidden"
              onChange={(e) => pickFile(e.target.files?.[0])} data-testid="portal-rx-flow-camera-input" />
            <input ref={fileRef} type="file" accept="image/*,application/pdf" className="absolute opacity-0 w-0 h-0 overflow-hidden"
              onChange={(e) => pickFile(e.target.files?.[0])} data-testid="portal-rx-flow-file-input" />
          </div>
        )}

        {step === 'preview' && file && (
          <div className="space-y-3" data-testid="portal-rx-flow-preview-step">
            {previewUrl ? (
              <img src={previewUrl} alt="Prescription preview" className="portal-flow-preview-img" data-testid="portal-rx-flow-preview-img" />
            ) : (
              <div className="portal-flow-preview-file" data-testid="portal-rx-flow-preview-file">
                <FileText size={22} className="text-slate-500" /><span className="truncate">{file.name}</span>
              </div>
            )}
            <Button variant="outline" className="w-full" onClick={() => setStepIdx(STEPS.indexOf('capture'))} data-testid="portal-rx-flow-retake">
              Choose a different file
            </Button>
          </div>
        )}

        {step === 'details' && (
          <div data-testid="portal-rx-flow-details-step">
            <label className="text-sm font-medium text-slate-700 block mb-1.5" htmlFor="portal-rx-flow-notes">Any notes for your pharmacist? (optional)</label>
            <Textarea id="portal-rx-flow-notes" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="portal-rx-flow-notes" />
          </div>
        )}

        {step === 'review' && (
          <dl className="space-y-0" data-testid="portal-rx-flow-review-step">
            <div className="portal-flow-review-row"><dt>Pharmacy</dt><dd data-testid="portal-rx-flow-review-destination">{destinationLabel || '—'}</dd></div>
            <div className="portal-flow-review-row"><dt>File</dt><dd className="flex items-center gap-1.5 justify-end">{(file?.type || '').startsWith('image/') ? <ImageIcon size={14} /> : <FileText size={14} />}<span className="truncate" data-testid="portal-rx-flow-review-file">{file?.name}</span></dd></div>
            {notes.trim() && <div className="portal-flow-review-row"><dt>Notes</dt><dd data-testid="portal-rx-flow-review-notes">{notes}</dd></div>}
          </dl>
        )}

        {step === 'confirmation' && (
          <div className="text-center space-y-3 pt-6" data-testid="portal-rx-flow-confirmation-step">
            <CheckCircle2 size={48} className="text-emerald-600 mx-auto" />
            <p className="text-base font-medium text-slate-800" data-testid="portal-rx-flow-confirmation-message">{confirmation}</p>
            <p className="text-sm text-slate-500">Your pharmacist will review it and message you.</p>
          </div>
        )}
      </div>

      <div className="portal-flow-sticky-action">
        {step === 'confirmation' ? (
          <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={onClose} data-testid="portal-rx-flow-done">Done</Button>
        ) : step !== 'capture' ? (
          <Button className="w-full bg-emerald-600 hover:bg-emerald-700"
            disabled={primaryDisabled}
            onClick={step === 'review' ? submit : goNext} data-testid="portal-rx-flow-primary">
            {step === 'review' && uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            {primaryLabel}
          </Button>
        ) : null}
      </div>
    </div>
  );
};
